// Damages — PI Platform outline v2 §1.7 / §3.17.
//
// What the claim is worth, itemized. Sits on Overview directly under
// Insurance for the reason stated there: policy limits are the ceiling
// on what a case can be worth, and this is the other half of that
// valuation. Both shape how the file is worked from day one — wage loss
// accrues during treatment, and a client asked about it eight months
// later remembers it wrong.
//
// Two things the display has to keep straight:
//
//   Past medicals are NOT editable here. They are derived from the
//   verified treatment encounters on the Providers tab. Showing them as
//   just another row invites someone to "correct" the total by adding a
//   second one, which would be added to the derived figure rather than
//   replacing it. So the row renders read-only and says where it comes
//   from.
//
//   Specials and generals stay in separate totals. Blending them into
//   one number is what lets a carrier discount the whole schedule.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const money = (v) =>
  v === null || v === undefined ? "—" : `$${Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const LABELS = {
  future_medicals: "Future medical care",
  lost_wages: "Lost wages",
  future_lost_wages: "Impaired earning capacity",
  out_of_pocket: "Out-of-pocket expenses",
  property_damage: "Property damage",
  household_services: "Household services",
  pain_suffering: "Pain and suffering",
  mental_anguish: "Mental anguish",
  disfigurement: "Disfigurement",
  loss_of_consortium: "Loss of consortium",
  loss_of_enjoyment: "Loss of enjoyment of life",
  punitive: "Punitive damages",
};

const BASIS_LABELS = {
  life_care_plan: "Life care plan",
  physician_estimate: "Physician estimate",
  economist_report: "Economist report",
  vocational_assessment: "Vocational assessment",
  employer_letter: "Employer letter",
  wage_statement: "Wage statement",
  tax_return: "Tax return",
  invoice: "Invoice",
  receipt: "Receipt",
  repair_estimate: "Repair estimate",
  attorney_estimate: "Attorney estimate",
  client_statement: "Client statement",
  other: "Other",
};

const EMPTY = { category: "", description: "", amount: "", basis_type: "" };

export function DamagesCard({ matterId, showNotification }) {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [vocab, setVocab] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch(`/api/matters/${matterId}/damages`);
      setRows(d.data || []);
      setSummary(d.summary || null);
    } catch (e) {
      showNotification?.(`Damages failed to load: ${e.message}`, "error");
      setRows([]);
    }
  }, [matterId, showNotification]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiFetch("/api/damage-categories").then(setVocab).catch(() => setVocab(null));
  }, []);

  const projected = vocab
    ? (vocab.inherently_projected || []).includes(draft.category)
    : false;
  const documented = vocab ? (vocab.documented_basis_types || []) : [];
  // A projection needs documented support to count toward the demand.
  // Saying so before the save is better than a warning afterwards.
  const willNotCount = projected && draft.basis_type
    && !documented.includes(draft.basis_type);

  const add = async () => {
    if (!draft.category || !draft.description.trim()) {
      showNotification?.("Pick a category and describe the loss.", "warning");
      return;
    }
    setSaving(true);
    try {
      await apiFetch(`/api/matters/${matterId}/damages`, {
        method: "POST",
        body: JSON.stringify({
          category: draft.category,
          description: draft.description.trim(),
          // Blank stays null, not 0 — an unquantified claim is real and
          // zero would read as worthless in every total.
          amount: draft.amount === "" ? null : Number(draft.amount),
          basis_type: draft.basis_type || null,
        }),
      });
      setDraft(EMPTY);
      await load();
    } catch (e) {
      showNotification?.(`Couldn't record that: ${e.message}`, "error");
    }
    setSaving(false);
  };

  const remove = async (d) => {
    if (!window.confirm(
      `Remove "${d.description}"? It stops being claimed in the demand.`)) return;
    try {
      await apiFetch(`/api/damages/${d.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      showNotification?.(`Delete failed: ${e.message}`, "error");
    }
  };

  const toggleClaimed = async (d) => {
    try {
      await apiFetch(`/api/damages/${d.id}`, {
        method: "PATCH", body: JSON.stringify({ is_claimed: !d.is_claimed }),
      });
      await load();
    } catch (e) {
      showNotification?.(`Update failed: ${e.message}`, "error");
    }
  };

  if (rows === null) {
    return React.createElement("div", { style: cardStyle },
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "8px" } },
        React.createElement(Spinner, { size: 16 }),
        React.createElement("span", { style: { fontSize: "13px", color: C.textMuted } },
          "Loading damages…")));
  }

  const inp = { padding: "7px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                fontSize: "13px", background: "#fff" };
  // `!= null` deliberately, catching undefined as well as null: if the
  // summary failed to load, `summary?.past_medicals?.amount` is
  // undefined, and a strict `!== null` would render the block and then
  // dereference a null summary inside it.
  const pastMedicals = summary?.past_medicals?.amount;
  const hasPastMedicals = pastMedicals != null;
  const economic = rows.filter(r => r.bucket === "economic");
  const nonEconomic = rows.filter(r => r.bucket === "non_economic");
  const punitive = rows.filter(r => r.bucket === "punitive");

  return React.createElement("div", { style: cardStyle },
    React.createElement("h2", { style: h2Style }, "Damages"),
    React.createElement("p", {
      style: { fontSize: "13px", color: C.textMuted, margin: "4px 0 14px" },
    }, rows.length === 0 && !hasPastMedicals
        ? "Nothing recorded yet. What goes here is everything the medical records can't show on their own — wage loss, future care, the non-economic claims."
        : "Feeds the demand. Medical bills come from the treatment records; everything else is entered here."),

    summary && (rows.length > 0 || hasPastMedicals) && React.createElement("div", {
      style: {
        display: "flex", gap: "22px", flexWrap: "wrap", padding: "12px 14px",
        background: C.warmWhite, borderRadius: "8px", marginBottom: "14px",
      },
    },
      _stat("Specials", money(summary.special_damages_total),
            "economic loss — medicals, wages, future care"),
      _stat("Generals", money(summary.general_damages_total),
            "non-economic — pain, consortium"),
      _stat("Total claimed", money(summary.total_claimed),
            "what the demand asks for"),
    ),

    // Derived, read-only. Rendering it as an editable row would invite a
    // second copy of the same money.
    hasPastMedicals && React.createElement("div", {
      style: {
        display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap",
        padding: "10px 13px", background: C.warmWhite,
        borderRadius: "8px", marginBottom: "14px",
      },
    },
      React.createElement("div", { style: { flex: "1 1 220px" } },
        React.createElement("div", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } },
          "Past medical bills"),
        React.createElement("div", { style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" } },
          `Calculated from ${summary.past_medicals.encounter_count} verified `
          + `encounter${summary.past_medicals.encounter_count === 1 ? "" : "s"} `
          + "· edit on the Providers tab"),
      ),
      React.createElement("div", {
        style: { fontSize: "12px", color: C.textDark, whiteSpace: "nowrap" },
      }, money(pastMedicals)),
    ),

    // Always-visible add row, same reasoning as case costs: a loss
    // recorded months later is recorded wrong or not at all.
    React.createElement("div", {
      style: {
        display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center",
        padding: "12px 14px", background: C.warmWhite, borderRadius: "8px",
        marginBottom: rows.length > 0 ? "14px" : 0,
      },
    },
      React.createElement("select", {
        value: draft.category,
        onChange: e => setDraft({ ...draft, category: e.target.value }),
        style: { ...inp, minWidth: "190px" },
      },
        React.createElement("option", { value: "" }, "Category…"),
        vocab && React.createElement("optgroup", { label: "Economic (specials)" },
          ...(vocab.economic || []).map(c =>
            React.createElement("option", { key: c, value: c }, LABELS[c] || c))),
        vocab && React.createElement("optgroup", { label: "Non-economic (generals)" },
          ...(vocab.non_economic || []).map(c =>
            React.createElement("option", { key: c, value: c }, LABELS[c] || c))),
        vocab && React.createElement("optgroup", { label: "Other" },
          ...(vocab.punitive || []).map(c =>
            React.createElement("option", { key: c, value: c }, LABELS[c] || c))),
      ),
      React.createElement("input", {
        type: "text", value: draft.description, placeholder: "Describe the loss",
        onChange: e => setDraft({ ...draft, description: e.target.value }),
        style: { ...inp, flex: "1 1 200px" },
      }),
      React.createElement("select", {
        value: draft.basis_type,
        onChange: e => setDraft({ ...draft, basis_type: e.target.value }),
        title: projected
          ? "A projected figure needs documented support to be claimed."
          : "How the number was arrived at (optional).",
        style: { ...inp, minWidth: "165px" },
      },
        React.createElement("option", { value: "" },
          projected ? "Basis (required)…" : "Basis…"),
        ...(vocab?.basis_types || []).map(b =>
          React.createElement("option", { key: b, value: b }, BASIS_LABELS[b] || b))),
      React.createElement("input", {
        type: "number", value: draft.amount, placeholder: "Amount", step: "0.01",
        onChange: e => setDraft({ ...draft, amount: e.target.value }),
        onKeyDown: e => { if (e.key === "Enter") add(); },
        title: "Leave blank if it isn't quantified yet — it stays tracked and out of the totals.",
        style: { ...inp, width: "120px" },
      }),
      React.createElement("button", {
        onClick: add, disabled: saving,
        style: {
          padding: "8px 16px", borderRadius: "8px", border: "none",
          background: saving ? C.warmGray : C.gold, color: "#fff",
          fontSize: "12px", fontWeight: 700, cursor: saving ? "default" : "pointer",
        },
      }, saving ? "Saving…" : "Record"),

      willNotCount && React.createElement("div", {
        style: { fontSize: "11px", color: C.textMuted, flexBasis: "100%" },
      }, "A projection on this basis is tracked but stays out of the demand total. "
       + "A life care plan, economist report, or employer letter makes it claimable."),
    ),

    _group("Economic", economic, { remove, toggleClaimed }),
    _group("Non-economic", nonEconomic, { remove, toggleClaimed }),
    _group("Punitive", punitive, { remove, toggleClaimed }),

    // Plain text, no colour coding — these are notes about completeness,
    // not severities.
    summary?.warnings?.length > 0 && React.createElement("ul", {
      style: {
        margin: "14px 0 0", paddingLeft: "18px", fontSize: "11px",
        color: C.textMuted, lineHeight: 1.6,
      },
    }, ...summary.warnings.map((w, i) =>
      React.createElement("li", { key: i }, w))),
  );
}

function _group(title, items, { remove, toggleClaimed }) {
  if (!items.length) return null;
  return React.createElement("div", { key: title, style: { marginTop: "14px" } },
    React.createElement("div", {
      style: { fontSize: "10px", color: C.textLight, fontWeight: 700,
               textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px" },
    }, title),
    React.createElement("div", {
      style: { border: `1px solid ${C.warmGray}60`, borderRadius: "8px", overflow: "hidden" },
    },
      ...items.map((d, i) => React.createElement("div", {
        key: d.id,
        style: {
          padding: "10px 13px", borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}40`,
          display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap",
          background: d.is_claimed ? "#fff" : C.warmWhite,
        },
      },
        React.createElement("div", { style: { flex: "1 1 220px", minWidth: 0 } },
          React.createElement("div", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } },
            LABELS[d.category] || d.category),
          React.createElement("div", {
            style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" },
          }, [
            d.description,
            d.basis_type ? BASIS_LABELS[d.basis_type] || d.basis_type : null,
            d.is_projected ? "projected" : null,
            !d.is_claimed ? "not claimed" : null,
          ].filter(Boolean).join(" · ")),
          // Says why the number isn't counting, next to the number.
          d.is_claimed && d.amount !== null && !d.is_supported
            && React.createElement("div", {
              style: { fontSize: "11px", color: C.textMuted, marginTop: "3px" },
            }, "Not in the total — a projection needs documented support."),
          d.is_claimed && d.amount === null && React.createElement("div", {
            style: { fontSize: "11px", color: C.textMuted, marginTop: "3px" },
          }, "Not quantified yet — tracked, not totalled."),
        ),
        React.createElement("div", {
          style: { fontSize: "12px", color: C.textDark, whiteSpace: "nowrap" },
        }, money(d.amount)),
        _quiet(d.is_claimed ? "Don't claim" : "Claim", () => toggleClaimed(d)),
        _quiet("Remove", () => remove(d)),
      )),
    ));
}

const cardStyle = {
  background: "#fff", borderRadius: "12px", padding: "20px 24px",
  border: "1px solid #E8E3DC", marginBottom: "20px",
};
const h2Style = {
  fontSize: "13px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.08em", fontWeight: 600, margin: 0,
};

function _stat(label, value, hint) {
  return React.createElement("div", { key: label, title: hint },
    React.createElement("div", {
      style: { fontSize: "10px", color: "#9A948C", fontWeight: 700,
               textTransform: "uppercase", letterSpacing: "0.06em" },
    }, label),
    React.createElement("div", {
      style: { fontSize: "16px", fontWeight: 700, color: "#1B2A4A", marginTop: "2px" },
    }, value),
    React.createElement("div", {
      style: { fontSize: "10px", color: "#9A948C", marginTop: "1px" },
    }, hint));
}

function _quiet(label, onClick) {
  return React.createElement("button", {
    key: label, onClick,
    style: {
      border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
      padding: "5px 11px", fontSize: "11px", fontWeight: 600,
      color: "#1B2A4A", cursor: "pointer", flexShrink: 0,
    },
  }, label);
}
